import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { getIdToken, onAuthStateChanged } from "../utils/firebaseAuth";
import useAuthModal from "../hooks/useAuthModal";
import PricingCard from "../components/pricing/PricingCard";
import {
  formatPriceWithLocale,
  chargeCurrency,
  getCurrencySymbol,
  getPrice,
  getPriceId,
  SUPPORTED_CURRENCIES,
} from "../utils/pricing";
import { currencyCodeFromHeaders } from "@/utils/geoCurrency";
import axios from "axios";
import { BASE_URL, getSubscriptionStatus } from "../utils/tools";
import connectToDb from "../utils/mongodb";
import { firebaseAdmin } from "../utils/firebaseAdmin";
import User from "../models/User";
import { resolveIsFirstTime } from "../utils/stripe";
import CreditCheckoutModal from "../components/modals/CreditCheckoutModal";
import { toast } from "@jammable/ui-core";
import { NextSeo } from "next-seo";
import { asLocale } from "@jammable/ui-core/src/i18n";
import { localeSeo } from "@/utils/localeSeo";
import { useT } from "@jammable/ui-core/src/i18n";

const PricingPage2 = ({ status, currency, isFirstTime }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [creditModal, setCreditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [intentType, setIntentType] = useState("payment");
  const router = useRouter();
  const { checkAuthAndExecute } = useAuthModal();
  const trialStarted = useRef(false);
  const t = useT();

  const refreshedForAuth = useRef(false);

  useEffect(() => {
    if (clientSecret) {
      setCreditModal(true);
    }
  }, [clientSecret]);

  // When the page is served to a logged-out visitor we can't know whether the
  // promo applies, so it renders optimistically. If they then sign in (usually
  // via the checkout modal), re-run getServerSideProps so the price reflects
  // their actual first-time status before they pay. Guarded by a ref because
  // the token cookie may lag the auth state by a tick.
  useEffect(() => {
    if (isFirstTime !== undefined) return;
    // { load: false }: signing in loads the Firebase SDK anyway; don't fetch
    // it up front for every logged-out visitor just to listen.
    const unsub = onAuthStateChanged(
      (fbUser) => {
        if (!fbUser || refreshedForAuth.current) return;
        refreshedForAuth.current = true;
        router.replace(router.asPath, undefined, { scroll: false });
      },
      { load: false },
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstTime]);

  // Abandoned-checkout recovery deep link: /pricing?trial=<plan>&t=<token>
  // opens checkout with a 7-day free trial (token verified server-side).
  useEffect(() => {
    const { trial, t } = router.query;
    if (!router.isReady || !trial || !t || trialStarted.current) return;

    const startTrialCheckout = async () => {
      if (trialStarted.current) return;
      trialStarted.current = true;
      try {
        const token = await getIdToken();
        const priceId = getPriceId(trial, currency, "month");
        const res = await axios.post(
          `/api/payment/createSubscriptionIntent`,
          {
            priceId,
            currency,
            from: "abandon-recovery-email",
            trialPlan: trial,
            trialToken: t,
          },
          { headers: { Authorization: token } },
        );
        setSelectedPlan({ plan: trial, price: getPrice(currency, trial) });
        setIntentType(res.data.intentType || "payment");
        if (res.data.intentType !== "setup") {
          // Token invalid/expired or minted for a different account — fall
          // back to regular checkout but say so instead of failing silently.
          toast.error(t("pricing.trialLinkInvalid"), { duration: 6000 });
        }
        setClientSecret(res.data.clientSecret);
      } catch (e) {
        trialStarted.current = false;
        console.error("Trial checkout failed", e);
      }
    };

    // Wait for Firebase to restore the session before deciding whether to
    // prompt auth — auth.currentUser is always null on first paint, which
    // would wrongly pop the sign-up modal for already-signed-in users.
    const unsub = onAuthStateChanged((fbUser) => {
      unsub();
      if (trialStarted.current) return;
      if (fbUser) {
        startTrialCheckout();
      } else {
        // Logged out -> sign-in modal; resumes the trial checkout after auth.
        checkAuthAndExecute(startTrialCheckout);
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.trial, router.query.t, currency]);

  const faqs = [
    {
      question: t("pricing.faq.noPlan.q"),
      answer: t("pricing.faq.noPlan.a"),
    },
    {
      question: t("pricing.faq.basic.q"),
      answer: t("pricing.faq.basic.a"),
    },
    {
      question: t("pricing.faq.creator.q"),
      answer: t("pricing.faq.creator.a"),
    },
    {
      question: t("pricing.faq.power.q"),
      answer: t("pricing.faq.power.a"),
    },
    {
      question: t("pricing.faq.cancel.q"),
      answer: t("pricing.faq.cancel.a"),
    },
    {
      question: t("pricing.faq.change.q"),
      answer: t("pricing.faq.change.a"),
    },
  ];

  // `getPrice(_, "<tier>")` is the discounted first-month amount and
  // `"<tier>-discount"` is the standing rate. The promo is only actually
  // applied for customers who have never paid, so anyone else must be shown
  // the standing rate as the headline - otherwise the page advertises $1.99
  // and Stripe charges $8.99.
  const showPromo = isFirstTime !== false;
  const headline = (tier) =>
    showPromo ? getPrice(currency, tier) : getPrice(currency, `${tier}-discount`);
  const struckThrough = (tier) =>
    showPromo ? getPrice(currency, `${tier}-discount`) : null;

  const fmt = (n) => formatPriceWithLocale(n, currency);

  const cards = [
    {
      plan: "basic",
      currency: chargeCurrency("basic", currency),
      price: headline("basic"),
      originalPrice: struckThrough("basic"),
      featured: false,
      badge: null,
      subtitle: t("pricing.basic.subtitle"),
      everythingIn: null,
      benefits: [
        t("pricing.feature.unlimitedCovers"),
        t("pricing.feature.download"),
        t("pricing.feature.library"),
        t("pricing.feature.tts"),
        t("pricing.feature.duets"),
        t("pricing.feature.credits"),
      ],
    },
    {
      plan: "creator",
      currency: chargeCurrency("creator", currency),
      price: headline("creator"),
      originalPrice: struckThrough("creator"),
      featured: true,
      badge: t("pricing.badge.popular"),
      subtitle: t("pricing.creator.subtitle"),
      everythingIn: "Basic",
      benefits: [
        t("pricing.feature.train"),
        t("pricing.feature.videos"),
        t("pricing.feature.coins50"),
      ],
    },
    {
      plan: "power",
      // Power has no Stripe price in some currencies and is charged in USD
      // there; show it in what it's charged in (see chargeCurrency).
      currency: chargeCurrency("power", currency),
      price: getPrice(chargeCurrency("power", currency), "power"),
      originalPrice: null,
      featured: false,
      badge: null,
      subtitle: t("pricing.power.subtitle"),
      everythingIn: "Creator",
      benefits: [
        t("pricing.feature.priority"),
        t("pricing.feature.coinsUnlimited"),
        t("pricing.feature.api"),
      ],
    },
  ];

  const unlimited = t("pricing.compare.unlimited");
  const compareRows = [
    [t("pricing.compare.browse"), "✓", "✓", "✓", "✓"],
    [t("pricing.compare.covers"), "—", unlimited, unlimited, unlimited],
    [t("pricing.compare.downloads"), "—", "✓", "✓", "✓"],
    [t("pricing.compare.ttsDuets"), "—", "✓", "✓", "✓"],
    [t("pricing.compare.song"), "—", "✓", "✓", "✓"],
    [t("pricing.compare.train"), "—", "—", unlimited, unlimited],
    [
      t("pricing.compare.videos"),
      "—",
      "—",
      t("pricing.compare.coins50"),
      unlimited,
    ],
    [
      t("pricing.compare.priority"),
      "—",
      "—",
      "—",
      t("pricing.compare.priorityLimit"),
    ],
    [t("pricing.compare.api"), "—", "—", "—", t("pricing.compare.apiAmount")],
  ];

  const title = t("pricing.seo.title");
  const description = t("pricing.seo.description");
  const seo = localeSeo(asLocale(router.locale), "https://www.jammable.com/pricing");

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={seo.canonical}
        languageAlternates={seo.languageAlternates}
        openGraph={{
          title,
          description,
          url: seo.canonical,
          locale: seo.ogLocale,
        }}
      />

      <div className="tw-bg-white tw-pb-28 tw-text-jam-ink md:tw-pb-16">
        <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-8 tw-px-4 tw-pt-4 md:tw-px-8 md:tw-pt-6">
          {/* ── Header ── */}
          <section className="tw-flex tw-flex-col tw-items-start tw-gap-4 md:tw-items-center md:tw-text-center">
            {showPromo && (
              <span className="tw-rounded-full tw-bg-jam-lime tw-px-3 tw-py-1.5 tw-text-xs tw-font-extrabold md:tw-px-3.5 md:tw-text-[13px]">
                {t("pricing.promo", {
                  price: fmt(getPrice(currency, "basic")),
                })}
              </span>
            )}
            <h1 className="jam-display tw-m-0 tw-text-[40px] tw-font-extrabold tw-leading-none tw-tracking-[-0.045em] md:tw-text-[68px]">
              {t("pricing.title")}
            </h1>
            <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-text-jam-muted md:tw-text-lg">
              {t("pricing.subtitle")}
            </p>
          </section>

          {/* ── Cards ── */}
          <section className="tw-grid tw-grid-cols-1 tw-items-stretch tw-gap-4 tw-pt-2 md:tw-grid-cols-[1fr_1.08fr_1fr]">
            {cards.map((card) => (
              <div
                key={card.plan}
                className={
                  card.plan === "creator"
                    ? "tw-order-first md:tw-order-none"
                    : card.plan === "power"
                      ? "tw-order-last md:tw-order-none"
                      : ""
                }
              >
                <PricingCard
                  plan={card.plan}
                  status={status}
                  currency={card.currency}
                  price={card.price}
                  originalPrice={card.originalPrice}
                  benefits={card.benefits}
                  everythingIn={card.everythingIn}
                  setClientSecret={setClientSecret}
                  onSelect={() => setSelectedPlan(card)}
                  featured={card.featured}
                  badge={card.badge}
                  subtitle={card.subtitle}
                />
              </div>
            ))}
          </section>

          <a
            href="#compare"
            className="tw-rounded-full tw-bg-jam-lav tw-p-3.5 tw-text-center tw-text-sm tw-font-extrabold tw-text-jam-ink tw-no-underline md:tw-hidden"
          >
            {t("pricing.compareAll")}
          </a>

          {/* ── Trust line ── */}
          <section className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-7 tw-gap-y-1 tw-text-center tw-text-xs tw-font-extrabold tw-text-jam-muted md:tw-text-sm">
            <span>{t("pricing.trust.cancel")}</span>
            <span aria-hidden="true">·</span>
            <span>{t("pricing.trust.stripe")}</span>
            <span aria-hidden="true">·</span>
            <span>{t("pricing.trust.currency")}</span>
          </section>

          {/* Checkout Modal */}
          <CreditCheckoutModal
            clientSecret={clientSecret}
            opened={creditModal}
            setOpened={setCreditModal}
            symbol={getCurrencySymbol(selectedPlan?.currency || currency)}
            currency={selectedPlan?.currency || currency}
            price={selectedPlan?.price}
            intentType={intentType}
          />

          {/* ── Compare plans ── */}
          <section
            id="compare"
            className="tw-flex tw-scroll-mt-24 tw-flex-col tw-gap-3.5"
          >
            <h2 className="jam-display tw-m-0 tw-text-[28px] tw-font-extrabold tw-tracking-[-0.03em] md:tw-text-[34px]">
              {t("pricing.compare.title")}
            </h2>
            <div className="tw-overflow-x-auto tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line md:tw-rounded-[32px]">
              <div className="tw-min-w-[640px]">
                <div className="tw-grid tw-grid-cols-[2fr_1fr_1fr_1fr_1fr] tw-gap-3 tw-bg-jam-lav tw-px-6 tw-py-4 tw-text-sm tw-font-extrabold">
                  <span />
                  <span>{t("pricing.compare.noPlan")}</span>
                  <span>Basic</span>
                  <span className="tw-text-jam-purple">Creator</span>
                  <span>Power</span>
                </div>
                {compareRows.map(([feature, none, basic, creator, power]) => (
                  <div
                    key={feature}
                    className="tw-grid tw-grid-cols-[2fr_1fr_1fr_1fr_1fr] tw-gap-3 tw-border-0 tw-border-t-2 tw-border-solid tw-border-jam-lav tw-px-6 tw-py-3.5 tw-text-sm tw-font-bold"
                  >
                    <span className="tw-font-extrabold">{feature}</span>
                    <span className="tw-text-jam-muted">{none}</span>
                    <span>{basic}</span>
                    <span>{creator}</span>
                    <span>{power}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section className="tw-grid tw-grid-cols-1 tw-gap-6 tw-rounded-[32px] tw-bg-jam-lav tw-p-5 md:tw-grid-cols-[1fr_1.6fr] md:tw-gap-8 md:tw-rounded-[40px] md:tw-p-9">
            <h2 className="jam-display tw-m-0 tw-text-[28px] tw-font-extrabold tw-leading-[1.05] tw-tracking-[-0.03em] md:tw-text-[34px]">
              {t("pricing.faq.title")}
            </h2>
            <div className="tw-flex tw-flex-col tw-gap-2">
              {faqs.map((faq) => (
                <details
                  key={faq.question}
                  className="tw-group tw-rounded-[22px] tw-bg-white tw-px-5 tw-py-[18px]"
                >
                  <summary className="tw-flex tw-cursor-pointer tw-list-none tw-items-center tw-justify-between tw-gap-4 tw-text-[15px] tw-font-extrabold [&::-webkit-details-marker]:tw-hidden">
                    <h3 className="tw-m-0 tw-text-[15px] tw-font-extrabold">
                      {faq.question}
                    </h3>
                    <span
                      aria-hidden="true"
                      className="tw-text-lg tw-text-jam-purple tw-transition-transform group-open:tw-rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <p className="tw-m-0 tw-mt-2.5 tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default PricingPage2;

export const getServerSideProps = async ({ req }) => {
  try {
    const token = req?.cookies?.token;
    let currency = "USD";
    let symbol = getCurrencySymbol(currency);

    const cfIp = req.headers["cf-connecting-ip"];
    const forwarded = req.headers["x-forwarded-for"];

    const ip =
      process.env.NODE_ENV === "production"
        ? cfIp
          ? Array.isArray(cfIp) ? cfIp[0] : cfIp
          : forwarded
            ? (Array.isArray(forwarded) ? forwarded[0] : forwarded).split(",")[0]
            : req.socket.remoteAddress
        : "86.23.252.218";

    // Prefer the Cloudflare/Vercel edge country header: free, instant, and
    // always present in production. The external IP lookup below is only a
    // fallback (local dev, or a request that somehow arrives without the
    // header) - it used to run on every request and block the page for up to
    // 3s, silently serving USD to everyone whenever the provider was slow.
    const headerCurrency = currencyCodeFromHeaders(req.headers);
    if (headerCurrency) {
      currency = headerCurrency;
      symbol = getCurrencySymbol(currency);
    } else {
      const timeout = 3000;
      const dataPromise = axios.get(
        `https://ipwhois.app/json/${ip}?fields=success,currency_code`,
      );

      const timeoutPromise = new Promise((resolve, reject) => {
        setTimeout(() => {
          reject(new Error("API call timed out"));
        }, timeout);
      });

      try {
        const data = await Promise.race([dataPromise, timeoutPromise]);
        const detectedCurrency =
          data?.data?.success && data?.data?.currency_code;

        if (detectedCurrency && SUPPORTED_CURRENCIES.includes(detectedCurrency)) {
          currency = detectedCurrency;
          symbol = getCurrencySymbol(currency);
        }
      } catch (error) {
        console.log(error);
        currency = "USD";
        symbol = getCurrencySymbol(currency);
      }
    }

    if (token) {
      const a = await firebaseAdmin.auth().verifyIdToken(token);
      await connectToDb();
      // The first-month promo is only applied at charge time when the customer
      // has never paid an invoice (createSubscriptionIntent -> resolveIsFirstTime).
      // Resolve the same flag here so returning customers aren't shown a promo
      // price they will not actually be charged.
      //
      // Runs alongside the profile fetch rather than after it - this is SSR on
      // a high-traffic page, and resolveIsFirstTime hits Stripe whenever the
      // cached hasPaidBefore flag isn't set yet (i.e. for every not-yet-paying
      // user). Defaults to showing the promo if it can't be resolved, which is
      // what ~90% of visitors are entitled to; the charge path is unaffected
      // either way.
      const [subData, isFirstTime] = await Promise.all([
        axios
          .get(`${BASE_URL}/api/user/getUserData`, {
            params: { firebaseUid: a.uid },
            headers: { Authorization: token },
          })
          .catch(() => {}),
        (async () => {
          try {
            const user = await User.findOne({ firebaseUid: a.uid });
            return user ? await resolveIsFirstTime(user) : true;
          } catch (e) {
            console.error("pricing: could not resolve first-time status", e);
            return true;
          }
        })(),
      ]);

      return {
        props: {
          status: getSubscriptionStatus(subData?.data?.subscriptions),
          currency,
          symbol,
          isFirstTime,
        },
      };
    } else {
      return { props: { currency, symbol } };
    }
  } catch (error) {
    return {
      props: {
        currency: "USD",
        symbol: getCurrencySymbol("USD"),
      },
    };
  }
};
